import { addDays, getYear, isSameDay } from 'date-fns';

export type HolidayType = 'NATIONAL' | 'REGIONAL';

export interface Holiday {
  date: Date;
  name: string;
  type: HolidayType;
  region?: 'SÜDTIROL' | 'ITALY';
}

// Fixed date holidays (same date every year)
const fixedHolidays: Array<Omit<Holiday, 'date'>> = [
  { name: 'Capodanno', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Epifania', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Festa della Liberazione', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Festa del Lavoro', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Festa della Repubblica', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Ferragosto', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Tutti i Santi', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Immacolata Concezione', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Natale', type: 'NATIONAL', region: 'ITALY' },
  { name: 'Santo Stefano', type: 'NATIONAL', region: 'ITALY' },
  // South Tyrolean holidays
  { name: 'Heilige Drei Könige', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Josef', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Georg', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Florian', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Maria Himmelfahrt', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Rupert', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Allerheiligen', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Heiliger Nikolaus', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Maria Empfängnis', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Weihnachten', type: 'REGIONAL', region: 'SÜDTIROL' },
  { name: 'Stephan', type: 'REGIONAL', region: 'SÜDTIROL' },
];

// Function to calculate Easter Sunday for a given year
function calculateEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// Function to get all holidays for a given year
export function getHolidaysForYear(year: number): Holiday[] {
  const holidays: Holiday[] = [];
  const easter = calculateEaster(year);

  // Add fixed date holidays
  fixedHolidays.forEach(holiday => {
    const date = new Date(year, 
      holiday.name === 'Capodanno' || holiday.name === 'Heilige Drei Könige' ? 0 :
      holiday.name === 'Epifania' ? 0 :
      holiday.name === 'Festa della Liberazione' ? 3 :
      holiday.name === 'Festa del Lavoro' ? 4 :
      holiday.name === 'Festa della Repubblica' ? 5 :
      holiday.name === 'Ferragosto' || holiday.name === 'Maria Himmelfahrt' ? 7 :
      holiday.name === 'Tutti i Santi' || holiday.name === 'Allerheiligen' ? 10 :
      holiday.name === 'Immacolata Concezione' || holiday.name === 'Maria Empfängnis' ? 11 :
      holiday.name === 'Natale' || holiday.name === 'Weihnachten' ? 11 :
      holiday.name === 'Santo Stefano' || holiday.name === 'Stephan' ? 11 : 0,
      holiday.name === 'Capodanno' ? 1 :
      holiday.name === 'Epifania' || holiday.name === 'Heilige Drei Könige' ? 6 :
      holiday.name === 'Festa della Liberazione' ? 25 :
      holiday.name === 'Festa del Lavoro' ? 1 :
      holiday.name === 'Festa della Repubblica' ? 2 :
      holiday.name === 'Ferragosto' || holiday.name === 'Maria Himmelfahrt' ? 15 :
      holiday.name === 'Tutti i Santi' || holiday.name === 'Allerheiligen' ? 1 :
      holiday.name === 'Immacolata Concezione' || holiday.name === 'Maria Empfängnis' ? 8 :
      holiday.name === 'Natale' || holiday.name === 'Weihnachten' ? 25 :
      holiday.name === 'Santo Stefano' || holiday.name === 'Stephan' ? 26 : 1
    );
    holidays.push({ ...holiday, date });
  });

  // Add Easter-related holidays
  holidays.push(
    { date: addDays(easter, -2), name: 'Venerdì Santo', type: 'NATIONAL', region: 'ITALY' },
    { date: addDays(easter, 1), name: 'Pasquetta', type: 'NATIONAL', region: 'ITALY' },
    { date: addDays(easter, 1), name: 'Ostermontag', type: 'REGIONAL', region: 'SÜDTIROL' },
    { date: addDays(easter, 50), name: 'Pentecoste', type: 'NATIONAL', region: 'ITALY' },
    { date: addDays(easter, 50), name: 'Pfingsten', type: 'REGIONAL', region: 'SÜDTIROL' }
  );

  return holidays;
}

// Function to get holidays for a date range
export function getHolidaysForRange(startDate: Date, endDate: Date): Holiday[] {
  const holidays: Holiday[] = [];
  const startYear = getYear(startDate);
  const endYear = getYear(endDate);

  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = getHolidaysForYear(year);
    holidays.push(...yearHolidays.filter(holiday => 
      holiday.date >= startDate && holiday.date <= endDate
    ));
  }

  return holidays;
}

// Function to check if a date is a holiday
export function isHoliday(date: Date, region?: 'SÜDTIROL' | 'ITALY'): Holiday | null {
  const holidays = getHolidaysForYear(getYear(date));
  return holidays.find(holiday => 
    isSameDay(holiday.date, date) && 
    (!region || holiday.region === region)
  ) || null;
} 